/*
 * Easy and custom implementation of /modules/mobius/src/utilities/breakpointMediaQueries.ts
 *
 *     Example:
 *
 *     - From BP:
 *         css: css`
 *             @media ${({ theme }) => mq("lg", theme)} {
 *                 background-color: red;
 *             }
 *         `
 *
 *         css: css`
 *             @media ${({ theme }) => mq({ $from: "lg" }, theme)} {
 *                 background-color: red;
 *             }
 *         `
 *
 *     - Until BP:
 *         css: css`
 *             @media ${({ theme }) => mq({ $until: "md" }, theme)} {
 *                 background-color: green;
 *             }
 *         `
 *
 *     - From & Until BP:
 *         css: css`
 *             @media ${({ theme }) => mq({ $from: "md", $until: "lg" }, theme)} {
 *                 background-color: blue;
 *             }
 *         `
 *
 * Set `breakpoints.values` & `breakpoints.maxWidths` from `setPreStyleGuideTheme` or `setPostStyleGuideTheme`.
 *
 * BP are:
 *     - xs - Mobile small
 *     - sm - Mobile
 *     - md - Tablet
 *     - lg - Desktop
 *     - xl - Wide
 */

import baseTheme from "@insite/mobius/globals/baseTheme";
import { BreakpointKey, breakpointKeys } from "@insite/mobius/utilities/breakpointMediaQueries";

export function mq(
    params: { $from?: BreakpointKey; $until?: BreakpointKey } | BreakpointKey,
    theme = baseTheme,
): string {
    if (typeof params === "string") {
        if (breakpointKeys.indexOf(params) === -1) {
            return "";
        }

        const screenSizeIdx = theme.breakpoints.keys.indexOf(params);
        const screenSize = theme.breakpoints.values[screenSizeIdx];
        return `screen and (min-width: ${screenSize}px)`;
    }

    const getScreenSize = (param: BreakpointKey): number => {
        const screenSizeIdx = theme.breakpoints.keys.indexOf(param);
        return theme.breakpoints.values[screenSizeIdx];
    };

    const { $from, $until } = params;
    if (
        ($from != null && breakpointKeys.indexOf($from) === -1) ||
        ($until != null && breakpointKeys.indexOf($until) === -1)
    ) {
        return "";
    }

    let query = "screen";
    if ($from != null) {
        query += ` and (min-width: ${getScreenSize($from)}px)`;
    }
    if ($until != null) {
        query += ` and (max-width: ${getScreenSize($until) - 1}px)`;
    }
    return query;
}

export function maxWidthValue(param: BreakpointKey, theme = baseTheme) {
    const maxWidthIdx = theme.breakpoints.keys.indexOf(param);
    return theme.breakpoints.maxWidths[maxWidthIdx];
}
